import { Button, DialogDescription, DialogHeader, DialogTitle, Icon } from "@nexus/ui"
import { useTranslation } from "react-i18next"

import type { MarketplaceOrderStatus } from "@/features/marketplace/types/marketplace.types"

export interface SendPendingRetryViewProps {
  vendorName: string
  status: MarketplaceOrderStatus
  retrying: boolean
  onRetry: () => void
  onViewOrder: () => void
  onClose: () => void
}

export function SendPendingRetryView({
  vendorName,
  status,
  retrying,
  onRetry,
  onViewOrder,
  onClose,
}: SendPendingRetryViewProps) {
  const { t } = useTranslation()
  const failed = status === "FAILED"

  return (
    <div className="flex flex-col items-center px-2 pt-2 pb-1 text-center">
      <span
        className={
          failed
            ? "mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10"
            : "mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-warning/10"
        }
      >
        <Icon
          name={failed ? "x-circle" : "loader"}
          size="lg"
          className={failed ? "text-destructive" : "animate-spin text-warning"}
        />
      </span>

      <DialogHeader className="items-center gap-1.5">
        <DialogTitle className="text-base font-medium">
          {t(
            failed
              ? "opportunities.scripts.sendDialog.pendingFailedTitle"
              : "opportunities.scripts.sendDialog.pendingTitle",
            failed ? "Send failed" : "Still sending",
          )}
        </DialogTitle>
        <DialogDescription className="max-w-[20rem] text-sm leading-relaxed">
          {t(
            failed
              ? "opportunities.scripts.sendDialog.pendingFailedBody"
              : "opportunities.scripts.sendDialog.pendingBody",
            failed
              ? "The handoff to {{vendorName}} failed. Retry the send from this order."
              : "The order exists but the partner email has not landed yet. Retry if it stays stuck.",
            { vendorName },
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
        <Button size="sm" onClick={onRetry} disabled={retrying}>
          {retrying
            ? t("opportunities.scripts.sendDialog.retrying", "Retrying…")
            : t("opportunities.scripts.sendDialog.retrySend", "Retry send")}
        </Button>
        <Button size="sm" variant="outline" onClick={onViewOrder}>
          {t("opportunities.scripts.sentCard.viewOrder")}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    </div>
  )
}
